import React, { useState, useEffect } from "react";
import { CasoRespuesta } from "@/api/entities";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Edit2, Trash2, Sparkles, ArrowUp, ArrowDown } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

const EMPTY_FORM = {
  nombre: "",
  descripcion: "",
  ejemplos_disparadores: [""],
  plantilla_respuesta: "",
  activo: true,
  orden: 0,
};

export default function Casos() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = creando, objeto = editando
  const [form, setForm] = useState(EMPTY_FORM);

  const { data: casos = [], isLoading } = useQuery({
    queryKey: ['casos-respuesta'],
    queryFn: () => CasoRespuesta.list({}, { orderBy: 'orden', orderDirection: 'asc' }),
  });

  const createMutation = useMutation({
    mutationFn: (data) => CasoRespuesta.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['casos-respuesta'] }); closeDialog(); toast({ title: 'Caso creado' }); },
    onError: (e) => toast({ title: 'Error creando caso', description: e.message, variant: 'destructive' }),
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => CasoRespuesta.update(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['casos-respuesta'] }); closeDialog(); toast({ title: 'Caso actualizado' }); },
    onError: (e) => toast({ title: 'Error actualizando', description: e.message, variant: 'destructive' }),
  });
  const deleteMutation = useMutation({
    mutationFn: (id) => CasoRespuesta.delete(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['casos-respuesta'] }); toast({ title: 'Caso eliminado' }); },
    onError: (e) => toast({ title: 'Error eliminando', description: e.message, variant: 'destructive' }),
  });

  // Sync del form al abrir dialog (crear o editar)
  useEffect(() => {
    if (!dialogOpen) return;
    if (editing) {
      setForm({
        nombre: editing.nombre || "",
        descripcion: editing.descripcion || "",
        ejemplos_disparadores: Array.isArray(editing.ejemplos_disparadores) && editing.ejemplos_disparadores.length > 0
          ? editing.ejemplos_disparadores : [""],
        plantilla_respuesta: editing.plantilla_respuesta || "",
        activo: editing.activo !== false,
        orden: Number(editing.orden) || 0,
      });
    } else {
      setForm({ ...EMPTY_FORM, orden: casos.length });
    }
  }, [dialogOpen, editing]);

  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (caso) => { setEditing(caso); setDialogOpen(true); };
  const closeDialog = () => { setDialogOpen(false); setEditing(null); };

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const updateDisparador = (idx, val) => {
    setForm((f) => {
      const next = [...f.ejemplos_disparadores];
      next[idx] = val;
      return { ...f, ejemplos_disparadores: next };
    });
  };
  const addDisparador = () => setForm((f) => ({ ...f, ejemplos_disparadores: [...f.ejemplos_disparadores, ""] }));
  const removeDisparador = (idx) => setForm((f) => ({
    ...f,
    ejemplos_disparadores: f.ejemplos_disparadores.filter((_, i) => i !== idx),
  }));

  const handleSubmit = () => {
    if (!form.nombre.trim()) {
      toast({ title: 'Falta el nombre', variant: 'destructive' });
      return;
    }
    if (!form.plantilla_respuesta.trim()) {
      toast({ title: 'Falta la plantilla de respuesta', variant: 'destructive' });
      return;
    }
    const data = {
      nombre: form.nombre.trim(),
      descripcion: form.descripcion.trim(),
      ejemplos_disparadores: form.ejemplos_disparadores.map(s => s.trim()).filter(Boolean),
      plantilla_respuesta: form.plantilla_respuesta,
      activo: !!form.activo,
      orden: Number(form.orden) || 0,
    };
    if (editing) updateMutation.mutate({ id: editing.id, data });
    else createMutation.mutate(data);
  };

  // Reordenar (subir/bajar). Updates batched serially.
  const moveCaso = async (caso, direction) => {
    const idx = casos.findIndex((c) => c.id === caso.id);
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (idx < 0 || targetIdx < 0 || targetIdx >= casos.length) return;
    const a = casos[idx];
    const b = casos[targetIdx];
    await updateMutation.mutateAsync({ id: a.id, data: { orden: targetIdx } });
    await updateMutation.mutateAsync({ id: b.id, data: { orden: idx } });
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-amber-500" />
            Casos de respuesta IA
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Configura los patrones que la IA usa para sugerir respuestas a emails de leads.
            La sugerencia siempre la revisa y envía un humano.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo caso
        </Button>
      </div>

      {isLoading ? (
        <div className="text-gray-500 dark:text-gray-400 text-sm">Cargando…</div>
      ) : casos.length === 0 ? (
        <Card className="dark:bg-gray-900 dark:border-gray-800">
          <CardContent className="py-12 text-center text-gray-500 dark:text-gray-400">
            <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p>Aún no hay casos configurados.</p>
            <p className="text-xs mt-1">Crea el primero con el botón "Nuevo caso".</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {casos.map((caso, idx) => (
            <Card key={caso.id} className={`dark:bg-gray-900 dark:border-gray-800 ${!caso.activo ? 'opacity-60' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono text-gray-400">#{idx + 1}</span>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{caso.nombre}</h3>
                      {caso.activo
                        ? <Badge className="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 border-green-200">Activo</Badge>
                        : <Badge variant="outline" className="text-gray-400">Inactivo</Badge>}
                    </div>
                    {caso.descripcion && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{caso.descripcion}</p>
                    )}
                    {Array.isArray(caso.ejemplos_disparadores) && caso.ejemplos_disparadores.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {caso.ejemplos_disparadores.map((e, i) => (
                          <span key={i} className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                            "{e}"
                          </span>
                        ))}
                      </div>
                    )}
                    {caso.plantilla_respuesta && (
                      <details className="mt-2">
                        <summary className="text-xs text-indigo-600 dark:text-indigo-400 cursor-pointer hover:underline">
                          Ver plantilla / brief
                        </summary>
                        <pre className="text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-2 rounded mt-1 whitespace-pre-wrap">
                          {caso.plantilla_respuesta}
                        </pre>
                      </details>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="ghost" size="sm" disabled={idx === 0} onClick={() => moveCaso(caso, 'up')} title="Subir">
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" disabled={idx === casos.length - 1} onClick={() => moveCaso(caso, 'down')} title="Bajar">
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                    <Switch
                      checked={!!caso.activo}
                      onCheckedChange={(checked) => updateMutation.mutate({ id: caso.id, data: { activo: checked } })}
                      title={caso.activo ? 'Desactivar' : 'Activar'}
                    />
                    <Button variant="ghost" size="sm" onClick={() => openEdit(caso)} title="Editar">
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" title="Eliminar">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Eliminar caso "{caso.nombre}"?</AlertDialogTitle>
                          <AlertDialogDescription>
                            La IA dejará de usarlo para sugerir respuestas. Esta acción no se puede deshacer.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-red-600 hover:bg-red-700"
                            onClick={() => deleteMutation.mutate(caso.id)}
                          >
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) closeDialog(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar caso' : 'Nuevo caso de respuesta'}</DialogTitle>
            <DialogDescription>
              La plantilla se usa como BRIEF para la IA, no como template literal. La IA adapta tono y contenido al cliente concreto.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Nombre del caso *</Label>
              <Input value={form.nombre} onChange={setField('nombre')} placeholder="Ej: Falta de datos" className="dark:bg-gray-800" />
            </div>
            <div>
              <Label className="text-xs">Descripción</Label>
              <Input value={form.descripcion} onChange={setField('descripcion')}
                placeholder="Cuándo aplica este caso (ayuda a la IA a clasificar)"
                className="dark:bg-gray-800" />
            </div>
            <div>
              <Label className="text-xs">Frases de ejemplo que disparan este caso</Label>
              <p className="text-[11px] text-gray-500 mb-1">
                Lo que un cliente típicamente escribe cuando estamos ante este caso. Cuantos más ejemplos, mejor clasifica la IA.
              </p>
              <div className="space-y-1">
                {form.ejemplos_disparadores.map((d, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      value={d}
                      onChange={(e) => updateDisparador(i, e.target.value)}
                      placeholder={`Ejemplo ${i + 1}`}
                      className="dark:bg-gray-800"
                    />
                    {form.ejemplos_disparadores.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeDisparador(i)} className="text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addDisparador}>
                  <Plus className="w-4 h-4 mr-1" />
                  Añadir ejemplo
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Plantilla / brief de respuesta *</Label>
              <p className="text-[11px] text-gray-500 mb-1">
                Cómo debería responder la IA cuando este caso aplica. Brief de estilo y contenido — la IA personaliza al cliente.
              </p>
              <Textarea
                value={form.plantilla_respuesta}
                onChange={setField('plantilla_respuesta')}
                rows={8}
                placeholder="Saluda al cliente por su nombre. Agradece la consulta. Pide los datos que faltan: tipo de producto, cantidad, medidas..."
                className="dark:bg-gray-800 font-mono text-xs"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={!!form.activo}
                onCheckedChange={(checked) => setForm((f) => ({ ...f, activo: checked }))}
              />
              <Label className="text-sm cursor-pointer" onClick={() => setForm((f) => ({ ...f, activo: !f.activo }))}>
                {form.activo ? 'Activo (la IA lo usará)' : 'Inactivo (la IA NO lo usará)'}
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancelar</Button>
            <Button
              onClick={handleSubmit}
              disabled={createMutation.isPending || updateMutation.isPending}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {editing ? 'Guardar cambios' : 'Crear caso'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
